import {
  IArIneligibility,
  IBorrowingBase,
  IExtraReserve,
  IInventoryIneligibilityCustomRule,
  IReserve,
} from './bbc'
import { IClientInfo } from './client'
import { INote } from './notes'
import { IUser } from './user'

export enum BBCLogType {
  Reserve = 'Reserve',
  AREligibility = 'AR Eligibility',
  CustomRule = 'Custom Rule',
  InventoryEligibility = 'Inventory Eligibility',
  InventoryMapping = 'Inventory Mapping',
  APEligibility = 'AP Eligibility',
}

export enum BBCLogAction {
  add = 'added',
  update = 'updated',
  delete = 'deleted',
}

export interface IBBCLogCustomRule {
  id: string
  action: BBCLogAction
  oldRuleId: string | null
  oldRule?: IInventoryIneligibilityCustomRule
  newRuleId: string | null
  newRule?: IInventoryIneligibilityCustomRule
}

export interface IBBCLogDebtorClientEligibility {
  id: string
  oldId: string | null
  oldItem?: IArIneligibility
  newId: string | null
  newItem?: IArIneligibility
  impactToAvailability: number
}

export interface IBBCLogInventoryIneligibilityCategories {
  id: string
  oldId: string | null
  oldItem?: {
    id: string
    category: string
    value: string
    eligible: boolean
  }
  newId: string | null
  newItem?: {
    id: string
    category: string
    value: string
    eligible: boolean
  }
  impactToAvailability: number
}

export interface IBBCLogMasterInventory {
  id: string
  oldId: string | null
  oldItem?: {
    id: string
    sku: string
    type: string
    brandCategory: string
    eligible: boolean
  }
  newId: string | null
  newItem?: {
    id: string
    sku: string
    type: string
    brandCategory: string
    eligible: boolean
  }
  impactToAvailability: number
}

export interface IBBCLogReserve {
  id: string
  action: BBCLogAction
  oldReserveId: string | null
  oldReserve?: IReserve
  newReserveId: string | null
  newReserve?: IReserve
}

export interface IBBCLogCreditorClient {
  id: string
  oldId: string | null
  oldItem?: IExtraReserve
  newId: string | null
  newItem?: IExtraReserve
  impactToAvailability: number
}

export interface IBBCLogUser {
  id: string
  userId: string
  user?: Pick<IUser, 'id' | 'firstName' | 'lastName'>
}

export interface IBBCLog {
  id: string
  clientName: string
  clientInfo?: Pick<IClientInfo, 'id'>
  borrowingBaseId: string | null
  borrowingBase?: Pick<IBorrowingBase, 'id' | 'isTest' | 'recordDate'>
  type: BBCLogType
  impactToAvailability: number
  users?: IBBCLogUser[]
  notes?: INote[]
  customRules: IBBCLogCustomRule[]
  debtorClientEligibility: IBBCLogDebtorClientEligibility[]
  inventoryIneligibilityCategories: IBBCLogInventoryIneligibilityCategories[]
  masterInventory: IBBCLogMasterInventory[]
  reserves: IBBCLogReserve[]
  creditorClients: IBBCLogCreditorClient[]
  createdAt: string
  updatedAt: string
}

export interface IBBCLogData {
  data: IBBCLog[]
  params: {
    clientName: string
  }
  totals: {
    totalItems: number
  }
}

export const BBC_LOG_FIELDS_TO_COMPARE = {
  [BBCLogType.CustomRule]: [
    'field',
    'label',
    'operator',
    'value',
    'eligible',
    'overrides',
    'values',
    'ineligibleReason',
  ],
  [BBCLogType.AREligibility]: ['creditLimits', 'concentrationLimit', 'category', 'eligible'],
  [BBCLogType.Reserve]: ['category', 'amount', 'kind', 'percent', 'field'],
  [BBCLogType.InventoryEligibility]: ['eligible', 'foreignCountry', 'isBankEligible'],
  [BBCLogType.InventoryMapping]: ['type', 'brandCategory', 'eligible'],
  [BBCLogType.APEligibility]: ['priorityPayable', 'notes'],
}

export const BBC_LOG_ITEM_DEFAULT: Partial<Record<BBCLogType, Record<string, unknown>>> = {
  [BBCLogType.AREligibility]: {
    category: null as string | null,
    creditLimits: 0,
    concentrationLimit: 0.3,
    eligible: null as boolean | null,
  },
  [BBCLogType.InventoryEligibility]: {
    eligible: true,
    foreignCountry: null as string | null,
    isBankEligible: true,
  },
  [BBCLogType.InventoryMapping]: {
    type: null as string | null,
    brandCategory: null as string | null,
    eligible: true,
  },
  [BBCLogType.APEligibility]: {
    priorityPayable: null as string | null,
    notes: null as string | null,
  },
}
